import { Alert } from '@ksyos/design-system';
import { OsasQuestionnaireName } from '../../../../../../../common/enums/osasQuestionnaireName';
import { translateKey } from '../../../../../../translate';
import { ScoreIndicator } from '../../../../../elements/scoreIndicator/ScoreIndicator';

interface IScoreRange {
    color: 'danger' | 'emergency' | 'bad' | 'normal' | 'warning' | 'emergency--darken';
    maxValue: number;
    minValue: number;
}

interface IOsasQuestionnaireScore {
    leftLabel: string;
    maxValue: number;
    minValue?: number;
    rightLabel: string;
    scoreRanges: IScoreRange[];
}

const fosq10Score: IOsasQuestionnaireScore = {
    maxValue: 20,
    minValue: 5,
    scoreRanges: [
        { minValue: 5, maxValue: 8, color: 'emergency' },
        { minValue: 9, maxValue: 12, color: 'danger' },
        { minValue: 13, maxValue: 16, color: 'bad' },
        { minValue: 17, maxValue: 20, color: 'normal' },
    ],
    leftLabel: '20',
    rightLabel: '5',
};

const essScore: IOsasQuestionnaireScore = {
    maxValue: 24,
    minValue: 0,
    scoreRanges: [
        { minValue: 0, maxValue: 5, color: 'normal' },
        { minValue: 6, maxValue: 10, color: 'warning' },
        { minValue: 11, maxValue: 12, color: 'danger' },
        { minValue: 13, maxValue: 15, color: 'bad' },
        { minValue: 16, maxValue: 24, color: 'emergency' },
    ],
    leftLabel: '0',
    rightLabel: '24',
};

const hsdqScore: IOsasQuestionnaireScore = {
    maxValue: 5,
    minValue: 1,
    scoreRanges: [
        { minValue: 1, maxValue: 2.02, color: 'normal' },
        { minValue: 2.02, maxValue: 5, color: 'emergency--darken' },
    ],
    leftLabel: '1',
    rightLabel: '5',
};

export type OsasQuestionnaireScoreType = 'fosq10' | 'ess' | 'hsdq';

export interface IOsasQuestionnaireScoreIndicatorProps {
    direction?: 'right' | 'left';
    norm?: string;
    onlyBadge?: boolean;
    osasQuestionnaireScoreType: OsasQuestionnaireScoreType;
    score: number;
    variant?: 'default' | 'gradient' | 'edgedGradient' | 'hsdqEdgedGradient';
}

export function OsasQuestionnaireScoreIndicator(props: IOsasQuestionnaireScoreIndicatorProps) {
    let osasQuestionnaireScore: IOsasQuestionnaireScore | undefined;
    switch (props.osasQuestionnaireScoreType) {
        case 'fosq10':
            osasQuestionnaireScore = fosq10Score;
            break;
        case 'ess':
            osasQuestionnaireScore = essScore;
            break;
        case 'hsdq':
            osasQuestionnaireScore = hsdqScore;
            break;
        default:
            break;
    }

    if (!osasQuestionnaireScore) {
        return null;
    }

    const scoreRange = getRange(osasQuestionnaireScore.scoreRanges, props.score);
    const minValue = osasQuestionnaireScore.minValue ?? 0;

    if (scoreRange) {
        return (
            <ScoreIndicator
                color={scoreRange.color}
                direction={props.direction}
                label={translateKey(`osas.${props.osasQuestionnaireScoreType}`)}
                leftLabel={osasQuestionnaireScore.leftLabel}
                onlyBadge={props.onlyBadge}
                percentage={((props.score - minValue) * 100) / (osasQuestionnaireScore.maxValue - minValue)}
                result={props.norm ? props.norm.replace(/\s\[.*\]/g, '') : ''}
                rightLabel={osasQuestionnaireScore.rightLabel}
                value={props.score}
                variant={props.variant}
                widerDot={props.osasQuestionnaireScoreType === OsasQuestionnaireName.hsdq}
            />
        );
    } else {
        return (
            <Alert appearance="warning">{`${translateKey(
                `osas.${props.osasQuestionnaireScoreType}`
            )}: geen interpretatie van ${translateKey(
                `osas.${props.osasQuestionnaireScoreType}`
            )} score mogelijk`}</Alert>
        );
    }
}

function getRange(ranges: IScoreRange[], score: number) {
    for (const range of ranges) {
        if (score >= range.minValue && score <= range.maxValue) {
            return range;
        }
    }
}
